import { formatDateOnly, type DateFormat } from '../settings/dateFormat';
import { t } from '../strings';
import { spanWords } from './wholeAnswer';

/**
 * 🔒 A person's Employments tile as a timeline (piece 13; T3097, decision 7480), the website's and the
 * phone's alike. The server sends one row per dated position, each carrying the employment it belongs
 * to; this one function folds them into one job per employment, the newest first, with the titles held
 * in it beneath. The words of every span and the rule for which dot is filled are made here, once.
 */
export interface JobTimelineRow {
  id?: string | null;
  /** The company, in the row's own weight. */
  label: string;
  /** The position's caption as the server writes it: the title, then its level, department, manager and reason. */
  value?: string | null;
  at?: string | null;
  until?: string | null;
  route?: string | null;
  detail?: string | null;
  /** Current on a position that is running, Ended on the day a job ends. */
  pill?: string | null;
  pillGroup?: 'good' | 'warning' | 'error' | 'inactive' | null;
  raw?: Record<string, unknown> | null;
}

/** One title held in a job, newest first. */
export interface JobTimelineTitle {
  title: string;
  /** The level, department, manager and reason the position names, joined; none where it names none. */
  extras: string | null;
  /** Its months in words, such as `November 2008 to July 2012`; none on a job that held one title. */
  when: string | null;
}

export interface JobTimelineJob<R extends JobTimelineRow = JobTimelineRow> {
  /** The employment's own id, or the row's where the answer names none. */
  key: string;
  company: string;
  route: string | null;
  /** The employment's days in the reader's date format, `Present` while it runs. */
  span: string;
  /** A running job draws a filled dot, an ended one a hollow dot. */
  running: boolean;
  pill: { text: string; group: 'good' | 'warning' | 'error' | 'inactive' } | null;
  titles: JobTimelineTitle[];
  /** The newest position's row, which carries the pencil, the pill and the remove of the job. */
  lead: R;
}

const text = (value: unknown): string | null => (typeof value === 'string' && value !== '' ? value : null);
const stamp = (value: string | null | undefined): number => {
  const parsed = value ? Date.parse(value) : Number.NaN;
  return Number.isFinite(parsed) ? parsed : Number.NEGATIVE_INFINITY;
};

/** The title and the rest of a position's caption. A row from an answer that carries no title of its own reads its whole caption as the title. */
function titleOf(row: JobTimelineRow, count: number): JobTimelineTitle {
  const raw = row.raw ?? {};
  const value = text(row.value);
  const when = count > 1 && row.at ? spanWords(row.at.slice(0, 7), row.until ? row.until.slice(0, 7) : null, !row.until) : null;
  if (typeof raw.title !== 'string') return { title: value ?? t('jobTimeline.untitled'), extras: null, when: when || null };
  const base = raw.title !== '' ? raw.title : t('jobTimeline.untitled');
  const rest = value !== null && value.startsWith(base) ? value.slice(base.length).replace(/^\s*·\s*/, '') : '';
  return { title: base, extras: rest !== '' ? rest : null, when: when || null };
}

/** The job's days: the employment's own start and end where the row carries them, else its earliest position's and the newest one's. */
function spanOf(group: readonly JobTimelineRow[], lead: JobTimelineRow, format: DateFormat): string {
  const raw = lead.raw ?? {};
  const starts = group.map((row) => row.at).filter((at): at is string => typeof at === 'string' && at !== '');
  const start = text(raw.startDate) ?? starts.sort((a, b) => (stamp(a) - stamp(b)) || 0)[0] ?? null;
  if (!start) return lead.detail ?? '';
  const end = 'endDate' in raw ? text(raw.endDate) : lead.until ?? null;
  return `${formatDateOnly(start, format)} – ${end ? formatDateOnly(end, format) : t('record.tile.present')}`;
}

/**
 * The jobs a person's Employments rows make: one per employment, the running ones first and then the
 * newest start, the titles of each newest first and a single title drawn alone. Two stays at one
 * company are two employments, so they are two jobs.
 */
export function jobTimeline<R extends JobTimelineRow>(rows: readonly R[], format: DateFormat): JobTimelineJob<R>[] {
  const groups = new Map<string, R[]>();
  rows.forEach((row, index) => {
    // 🔒 Decision 9861: an agreement row shares the Employments tile's list and is no job, so the fold leaves it for the page to draw as a link.
    if (text(row.raw?.agreementId) !== null) return;
    const key = text(row.raw?.employmentId) ?? row.id ?? `row-${index}`;
    const held = groups.get(key);
    if (held) held.push(row);
    else groups.set(key, [row]);
  });
  const jobs = [...groups.entries()].map(([key, held]) => {
    // Newest position first; positions that start on one day keep the order the server sent, so the pencil stays on the row it was on.
    const ordered = held.map((row, index) => ({ row, index })).sort((a, b) => stamp(b.row.at) - stamp(a.row.at) || a.index - b.index).map(({ row }) => row);
    const lead = ordered[0]!;
    return {
      key,
      company: lead.label,
      route: lead.route ?? null,
      span: spanOf(ordered, lead, format),
      running: lead.pill === 'Current',
      pill: lead.pill ? { text: lead.pill, group: lead.pillGroup ?? 'inactive' } : null,
      titles: ordered.map((row) => titleOf(row, ordered.length)),
      lead,
      began: Math.min(...ordered.map((row) => stamp(text(row.raw?.startDate) ?? row.at))),
    };
  });
  return jobs
    .sort((a, b) => Number(b.running) - Number(a.running) || (b.began - a.began) || 0)
    .map(({ began: _began, ...job }) => job);
}
